import { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { CustomSelect } from "../components/CustomSelect";
import type { CustomSelectOption } from "../components/CustomSelect";
import { ApiError, audit as auditApi } from "../lib/api";
import type { AuditEntry } from "../lib/api";

const ACTION_LABEL: Record<string, string> = {
  "auth.login": "Inicio de sesión",
  "auth.funcionario_login": "Ingreso de funcionario",
  "auth.password_changed": "Cambio de contraseña",
  "auth.totp_enabled": "2FA activado",
  "auth.totp_disabled": "2FA desactivado",
  "tenant.create": "Alta de alcaldía",
  "tenant.update": "Actualización de alcaldía",
  "tenant.impersonate_begin": "Inicio de impersonación",
  "tenant.impersonate_end": "Fin de impersonación",
  "pqrsdf.create": "Radicado creado",
  "pqrsdf.update": "Radicado actualizado",
  "pqrsdf.reply": "Respuesta enviada",
};

const ACTION_COLOR: Record<string, string> = {
  "auth.login": "var(--info)",
  "auth.funcionario_login": "var(--info)",
  "auth.password_changed": "var(--warn)",
  "auth.totp_enabled": "var(--ok)",
  "auth.totp_disabled": "var(--err)",
  "tenant.create": "var(--ok)",
  "tenant.update": "var(--info)",
  "tenant.impersonate_begin": "var(--warn)",
  "tenant.impersonate_end": "var(--ink-3)",
  "pqrsdf.create": "var(--ok)",
  "pqrsdf.update": "var(--info)",
  "pqrsdf.reply": "var(--info)",
};

const FIELD_LABEL: Record<string, string> = {
  estado: "Estado",
  usuario_asignado_id: "Asignado a",
  must_change_password: "Debe cambiar contraseña",
  nombre: "Nombre",
  slug: "Slug",
  activo: "Activo",
  email: "Email",
  rol: "Rol",
  prioridad: "Prioridad",
  canal: "Canal",
  asunto: "Asunto",
  target_tenant_id: "Alcaldía",
  target_slug: "Alcaldía",
};

const ESTADO_LABEL: Record<string, string> = {
  radicado: "Radicado",
  en_proceso: "En proceso",
  respondido: "Respondido",
  cerrado: "Cerrado",
  vencido: "Vencido",
};

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function fmtValue(field: string, v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    if (field === "estado" && ESTADO_LABEL[v]) return ESTADO_LABEL[v];
    if (isUuid(v)) return v.slice(0, 8) + "…";
    return v;
  }
  return JSON.stringify(v);
}

type ChangeEntry = { from?: unknown; to?: unknown } | unknown;

function isFromTo(v: unknown): v is { from?: unknown; to?: unknown } {
  return typeof v === "object" && v !== null && ("from" in v || "to" in v);
}

const ACTION_OPTS: ReadonlyArray<CustomSelectOption<string>> = [
  { value: "", label: "Todas las acciones" },
  { value: "auth.login", label: "Inicios de sesión" },
  { value: "auth.password_changed", label: "Cambios de contraseña" },
  { value: "auth.totp_enabled", label: "2FA activado" },
  { value: "auth.totp_disabled", label: "2FA desactivado" },
  { value: "tenant.create", label: "Altas de alcaldía" },
  { value: "tenant.update", label: "Actualizaciones de alcaldía" },
];

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AuditLogPage() {
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await auditApi.list({
        action: action || undefined,
        limit: 100,
      });
      setItems(resp.items);
      setTotal(resp.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">Plataforma · Auditoría</div>
        <h1>Bitácora</h1>
        <p className="lede">
          Cada acción del super_admin y de los funcionarios queda registrada
          aquí con su huella temporal, IP y diferencias. {total} entrada
          {total === 1 ? "" : "s"} en total.
        </p>

        <div style={{ display: "flex", gap: 12, marginBottom: 22 }}>
          <div style={{ maxWidth: 280 }}>
            <CustomSelect
              value={action}
              onChange={(v) => setAction(v)}
              options={ACTION_OPTS}
              aria-label="Filtrar por acción"
            />
          </div>
        </div>

        {error && <div className="alert err">{error}</div>}

        <div className="card">
          <div className="card-inner" style={{ padding: 0 }}>
            {loading ? (
              <p style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
                Cargando…
              </p>
            ) : items.length === 0 ? (
              <p style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
                Sin entradas.
              </p>
            ) : (
              <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {items.map((it) => (
                  <li
                    key={it.id}
                    style={{
                      borderBottom: "1px solid var(--hairline)",
                      padding: "18px 24px",
                      display: "grid",
                      gridTemplateColumns: "160px 200px 1fr",
                      gap: 20,
                      alignItems: "start",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        letterSpacing: "0.1em",
                        color: "var(--ink-3)",
                        textTransform: "uppercase",
                      }}
                    >
                      {fmtDate(it.created_at)}
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color: ACTION_COLOR[it.action] ?? "var(--ink-2)",
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        {ACTION_LABEL[it.action] ?? it.action}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                        {it.actor_email}
                        {it.ip_address && (
                          <>
                            {" · "}
                            <span style={{ fontFamily: "var(--font-mono)" }}>
                              {it.ip_address}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      {Object.keys(it.changes).length > 0 && (
                        <ul
                          style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            display: "grid",
                            gap: 6,
                          }}
                        >
                          {Object.entries(it.changes as Record<string, ChangeEntry>).map(
                            ([field, raw]) => {
                              const label = FIELD_LABEL[field] ?? field;
                              if (isFromTo(raw)) {
                                const from = fmtValue(field, raw.from);
                                const to = fmtValue(field, raw.to);
                                return (
                                  <li
                                    key={field}
                                    style={{
                                      display: "flex",
                                      gap: 10,
                                      alignItems: "baseline",
                                      flexWrap: "wrap",
                                      fontSize: 13,
                                    }}
                                  >
                                    <span style={{ color: "var(--ink-3)", minWidth: 130 }}>
                                      {label}
                                    </span>
                                    <span
                                      style={{
                                        fontFamily: "var(--font-mono)",
                                        color: "var(--ink-3)",
                                        textDecoration:
                                          raw.from !== null && raw.from !== undefined
                                            ? "line-through"
                                            : "none",
                                      }}
                                      title={typeof raw.from === "string" ? raw.from : undefined}
                                    >
                                      {from}
                                    </span>
                                    <span style={{ color: "var(--ink-3)" }}>→</span>
                                    <span
                                      style={{
                                        fontFamily: "var(--font-mono)",
                                        color: "var(--ink-1)",
                                        fontWeight: 600,
                                      }}
                                      title={typeof raw.to === "string" ? raw.to : undefined}
                                    >
                                      {to}
                                    </span>
                                  </li>
                                );
                              }
                              return (
                                <li
                                  key={field}
                                  style={{
                                    display: "flex",
                                    gap: 10,
                                    alignItems: "baseline",
                                    fontSize: 13,
                                  }}
                                >
                                  <span style={{ color: "var(--ink-3)", minWidth: 130 }}>
                                    {label}
                                  </span>
                                  <span
                                    style={{
                                      fontFamily: "var(--font-mono)",
                                      color: "var(--ink-1)",
                                    }}
                                  >
                                    {fmtValue(field, raw)}
                                  </span>
                                </li>
                              );
                            },
                          )}
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </section>
    </Shell>
  );
}
